/* eslint-disable no-sparse-arrays */
import React, { useMemo } from 'react';
import numeral from 'numeral';
import uppercase from 'lodash.uppercase';
import startCase from 'lodash.startcase';
import padLeft from 'pad-left';
import { confirmAlert } from 'react-confirm-alert';
import JwtDecode from 'jwt-decode';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import momentTimezone from 'moment-timezone';
import truncate from 'lodash.truncate';
import { Field, Form } from 'react-final-form';
import Select from 'react-select';
import axios from 'axios';
import SSRStorage from './storage';
import { alphabets, API_URI, emailRegex, patientAPI, PAYPOINT, phoneRegex, TOKEN_COOKIE, VAT } from './constants';
import placeholder from '../assets/images/placeholder.jpg';
import { hasViewAppointmentPermission } from '../permission-utils/appointment';
import { notifyError } from './notify';
import { Statistic } from 'antd';
import TableLoading from '../components/TableLoading';
import ModalHeader from '../components/ModalHeader';
import { Modal } from 'react-bootstrap';

export const usePagination = (data, currentPage, pageSize) => {
	return useMemo(() => {
		const start = (currentPage - 1) * pageSize;
		return data.slice(start, start + pageSize);
	}, [data, currentPage, pageSize]);
};

export const formatCurrencyBare = (amount, abs) => {
	if (!amount) {
		return '0.00';
	}
	return `${numeral(abs ? Math.abs(amount) : amount).format('0,0.00')}`;
};

export const formatCurrency = (amount, abs) => {
	if (!amount) {
		return '₦0.0';
	}
	return `₦${numeral(abs ? Math.abs(amount) : amount).format('0,0.00')}`;
};

export const formatCurrencyNew = (amount, abs) => {
	if (!amount) return '₦0';

	const value = abs ? Math.abs(amount) : amount;

	return `₦${numeral(value).format('0.[0]a').toUpperCase()}`;
};

export function formatCamelCaseToText(str = '') {
	if (!str) return '';

	// Insert space before capital letters and trim extra spaces
	const formatted = str
		.replace(/([A-Z])/g, ' $1') // add space before capital letters
		.replace(/^./, c => c.toUpperCase()) // capitalize first letter
		.trim();

	return formatted;
}

export function formatToSpan(str = '') {
	if (!str) return '';

	return <span className="">({str})</span>;
}

export function formatHyphenatedText(text) {
	if (!text) return '';
	return text
		.split('-')
		.map(word => word.charAt(0).toUpperCase() + word.slice(1))
		.join(' ');
}

export const isUnset = o => typeof o === 'undefined' || o === null;

export const isSet = o => !isUnset(o);

export function encodeValue(val) {
	if (typeof val === 'string') {
		return val;
	}

	return JSON.stringify(val);
}

export async function getData2(patient, title) {
	const res = await request(`${patientAPI}/${patient.id}/vitals`, 'GET', true);
	return res.find(c => c.readingType === title);
}

export function decodeValue(val) {
	if (typeof val === 'string') {
		try {
			return JSON.parse(val);
		} catch (_) {}
	}

	return val;
}

const checkStatus = async response => {
	if (!response.ok) {
		if (response.statusText === 'Unauthorized') {
			// prettier-ignore
			(new SSRStorage()).removeItem(TOKEN_COOKIE);
			window.location.reload(true);
		}

		const message = await response.text();

		const err = JSON.parse(message);
		throw Object.freeze({ message: err.message || err.error });
	}

	return response;
};

export const defaultHeaders = {
	Accept: 'application/json',
	'Access-Control-Allow-Methods': 'GET, POST, PUT, DELETE, PATCH',
	'Content-Type': 'application/json',
	'ngrok-skip-browser-warning': true,
	'bypass-tunnel-reminder': true,
};

export const defaultUploadHeaders = {
	Accept: 'application/json',
	'Access-Control-Allow-Methods': 'GET, POST, PUT, DELETE, PATCH',
	//'Content-Type': 'application/x-www-form-urlencoded',
	'Content-Type': 'multipart/form-data',
	'ngrok-skip-browser-warning': true,
	'bypass-tunnel-reminder': true,
};

// export const patchHeaders = {
// 	Accept: 'application/json',
// 	'Access-Control-Allow-Origin': '*',
// 	'Content-Type': 'application/json',
// };

const headers = user => {
	if (user) {
		const jwt = `Bearer ${user.token}`;
		return { ...defaultHeaders, Authorization: jwt };
	} else {
		return defaultHeaders;
	}
};

const parseJSON = response => response.json();

export const requestPatch = async (url, authed = false, data) => {
	axios.defaults.headers.post['Access-Control-Allow-Origin'] = 'GET, POST,HEAD, OPTIONS,PUT, DELETE';
	axios.defaults.headers.post['Access-Control-Request-Headers'] = '*';
	axios.defaults.headers.post['Content-Type'] = 'application/json';
	axios.defaults.headers.post['Accept'] = 'application/json';
	if (authed) {
		// prettier-ignore
		const user = await (new SSRStorage()).getItem(TOKEN_COOKIE);
		axios.defaults.headers.common['Authorization'] = user.token;
	}
	const result = await axios.patch(url, data);
	return parseJSON(result);
};

export const request = async (url, method, authed = false, data) => {
	// prettier-ignore
	const user = await (new SSRStorage()).getItem(TOKEN_COOKIE);
	console.log(url);
	const response = await fetch(`${API_URI}/${url}`, {
		method: method,
		headers: authed ? headers(user) : { ...defaultHeaders },
		body: JSON.stringify(data),
	});
	const result = await checkStatus(response);
	return parseJSON(result);
};

// return plantext
export const requestAlt = async (url, method, authed = false, data) => {
	const user = await new SSRStorage().getItem(TOKEN_COOKIE);
	console.log(url);

	const response = await fetch(`${API_URI}/${url}`, {
		method,
		headers: authed ? headers(user) : { ...defaultHeaders },
		body: data ? JSON.stringify(data) : undefined,
	});

	const result = await checkStatus(response);

	// Always return plain text
	return await result.text();
};

export const QuickBooksrequest = async (url, data, qbo) => {
	// prettier-ignore
	const user = await (new SSRStorage()).getItem(TOKEN_COOKIE);
	// console.log(user);
	const response = await fetch(`${API_URI}/${url}`, {
		method: 'POST',
		headers: { ...headers(user), qbo: JSON.stringify(qbo) },
		body: JSON.stringify(data),
	});

	const result = await checkStatus(response);
	return parseJSON(result);
};

export const upload = async (url, method, body) => {
	// prettier-ignore
	const user = await (new SSRStorage()).getItem(TOKEN_COOKIE);
	const jwt = `Bearer ${user.token}`;
	let headers = { Authorization: jwt };
	const response = await fetch(url, { method, headers, body });
	const result = await checkStatus(response);
	return parseJSON(result);
};

export const uploadFile = async (url, body) => {
	// prettier-ignore
	const user = await (new SSRStorage()).getItem(TOKEN_COOKIE);
	const jwt = `Bearer ${user.token}`;
	let headers = { Authorization: jwt };
	const response = axios.post(`${API_URI}/${url}`, body, { headers });
	// const response = await fetch(url, { method, headers, body });
	return response;
};

export const updateImmutable = (list, payload) => {
	const data = list.find(d => d?.id === payload?.id);
	if (data) {
		const index = list.findIndex(d => d?.id === payload?.id);

		return [...list.slice(0, index), { ...data, ...payload }, ...list.slice(index + 1)];
	}

	return list;
};

export const getPageList = (array, page_size, page_number) => {
	return array.slice((page_number - 1) * page_size, page_number * page_size);
};

// prettier-ignore
export const renderTextInput = ({ className, input, label, type, id, placeholder, disabled, readOnly = false, autoComplete, meta: { touched, error } }) => (
	<div
		className={`form-group ${touched &&
			(error ? 'has-error has-danger' : '')} ${className ? className : ''}`}>
		<label htmlFor={id}>{label}</label>
		<input
			{...input}
			type={type}
			className="form-control"
			placeholder={placeholder || label}
			readOnly={readOnly}
			disabled={disabled}
			autoComplete={autoComplete}
		/>
		{touched && error && (
			<div className="help-block form-text with-errors form-control-feedback">
				<ul className="list-unstyled">
					<li>{error}</li>
				</ul>
			</div>
		)}
	</div>
);

export const renderTextArea = ({ input, label, type, id, placeholder, meta: { touched, error } }) => (
	<div className={`form-group ${touched && (error ? 'has-error has-danger' : '')}`}>
		<label htmlFor={id}>{label}</label>
		<textarea {...input} type={type} className="form-control" placeholder={placeholder || label}></textarea>
		{touched && error && (
			<div className="help-block form-text with-errors form-control-feedback">
				<ul className="list-unstyled">
					<li>{error}</li>
				</ul>
			</div>
		)}
	</div>
);

// prettier-ignore
export const renderTextInputGroup = ({ input, append, label, icon, type, id, placeholder, autoComplete, meta: { touched, error } }) => (
	<div
		className={`form-group ${touched &&
			(error ? 'has-error has-danger' : '')}`}>
		<label htmlFor={id}>{label}</label>
		<div className="input-group">
			{!append && (
				<div className="input-group-prepend">
					<div className="input-group-text">{icon}</div>
				</div>
			)}
			<input
				{...input}
				type={type}
				className="form-control"
				placeholder={placeholder || label}
				autoComplete={autoComplete}
			/>
			{append && (
				<div className="input-group-append">
					<div className="input-group-text">{icon}</div>
				</div>
			)}
		</div>
		{touched && error && (
			<div className="help-block form-text with-errors form-control-feedback">
				<ul className="list-unstyled">
					<li>{error}</li>
				</ul>
			</div>
		)}
	</div>
);

export const formatPatientId = data => {
	if (!data) {
		return '';
	}
	if (data.is_out_patient) {
		return 'UNREG';
	}

	let formattedId = String(data.id);
	let len = 7 - formattedId.length;
	while (len >= 0) {
		formattedId = '0' + formattedId;
		len--;
	}

	const legacyId =
		data.legacy_patient_id && data.legacy_patient_id !== '' ? (+data.legacy_patient_id ? ` [${data.legacy_patient_id}]` : formattedId) : '';

	return legacyId ? legacyId : formattedId;
};

export const renderDateTimePicker = ({ input, placeholder, minDate, maxDate }) => (
	<div className="custom-date-input">
		<DatePicker
			className="single-daterange form-control"
			dateFormat="yyyy/MM/dd"
			selected={input.value || null}
			onChange={input.onChange}
			minDate={minDate}
			maxDate={maxDate}
			disabledKeyboardNavigation
			placeholderText={placeholder}
		/>
	</div>
);

export const renderTimePicker = ({ input, placeholder, minDate, maxDate }) => (
	<div className="custom-date-input">
		<DatePicker
			className="single-daterange form-control"
			dateFormat="Pp"
			selected={input.value || null}
			onChange={input.onChange}
			disabledKeyboardNavigation
			placeholderText={placeholder}
		/>
	</div>
);

export const confirmAction = (action, payload, alertText, alertHead) => {
	confirmAlert({
		customUI: ({ onClose }) => {
			const onclick = async () => {
				action(payload);
				onClose();
			};

			return (
				<div className="custom-ui text-center">
					<h1 className="">{alertHead ? alertHead : 'Are you sure?'}</h1>
					<p>{alertText ? alertText : 'You want to delete this'}</p>
					<div>
						<button className="btn btn-danger" style={{ margin: '10px' }} onClick={onClose}>
							No
						</button>
						<button className="btn btn-primary" style={{ margin: '10px' }} onClick={onclick}>
							Yes
						</button>
					</div>
				</div>
			);
		},
	});
};

export const confirmActionAlt = (action, payload, alertText, alertHead) => {
	confirmAlert({
		customUI: ({ onClose }) => {
			const onclick = async () => {
				action(payload);
				onClose();
			};

			return (
				<div className="custom-ui text-center">
					<h1 className="">{alertHead ? alertHead : 'Are you sure?'}</h1>
					<p>{alertText ? alertText : 'You want to delete this'}</p>
					<div>
						<button className="btn btn-danger" style={{ margin: '10px' }} onClick={onClose}>
							Cancel
						</button>
						<button className="btn btn-primary" style={{ margin: '10px' }} onClick={onclick}>
							Yes
						</button>
					</div>
				</div>
			);
		},
	});
};

export const confirmTransfer = (action, payload, alertText, alertHead) => {
	const [itemId, itemAmount, itemAmountPaid] = payload;

	confirmAlert({
		customUI: ({ onClose }) => {
			const TransferModal = () => {
				const totalAmount = Math.abs(Number(itemAmount || 0));
				const initialTransferAmount = Math.abs(Number(itemAmountPaid || 0));
				const [amountPaid, setAmountPaid] = React.useState(
					initialTransferAmount > 0 ? Math.min(initialTransferAmount, totalAmount) : totalAmount
				);

				const balance = Math.max(totalAmount - (Number(amountPaid) || 0), 0);

				const onclick = async () => {
					const transferAmount = Number(amountPaid || 0);

					if (transferAmount <= 0) {
						notifyError('Please enter a valid transfer amount');
						return;
					}

					if (transferAmount > totalAmount) {
						notifyError('Transfer amount cannot be more than transaction amount');
						return;
					}

					action([itemId, itemAmount, transferAmount]);
					onClose();
				};

				return (
					<div className="onboarding-modal modal fade animated show" role="dialog" style={{ display: 'block' }}>
						<div className="modal-dialog modal-centered">
							<div className="modal-content">
								<ModalHeader title={alertHead || 'Transfer To Paypoint'} closeModal={onClose} />
								<div className="onboarding-content with-gradient" style={{ padding: '0 20px 20px 20px' }}>
									<p className="text-muted" style={{ marginBottom: '15px' }}>
										{alertText || 'Choose amount to transfer to paypoint. Remaining value stays on HMO transaction.'}
									</p>

									<div
										style={{
											display: 'grid',
											gridTemplateColumns: '1fr 1fr',
											gap: '10px',
											marginBottom: '15px',
										}}
									>
										<div className="p-2" style={{ border: '1px solid #ececec', borderRadius: '8px' }}>
											<small className="text-muted d-block">Total Amount</small>
											<strong>{formatCurrency(totalAmount)}</strong>
										</div>
										<div className="p-2" style={{ border: '1px solid #ececec', borderRadius: '8px' }}>
											<small className="text-muted d-block">Remaining On HMO</small>
											<strong>{formatCurrency(balance)}</strong>
										</div>
									</div>

									<div className="form-block">
										<Form
											onSubmit={onclick}
											initialValues={{
												amount: totalAmount,
												amount_paid: amountPaid,
											}}
											render={({ handleSubmit }) => (
												<form onSubmit={handleSubmit}>
													<div className="row">
														<div className="col-sm-12">
															<Field name="amount" component={renderTextInput} type="number" label="Amount" disabled={true} />

															<Field
																name="amount_paid"
																render={({ input, meta }) => (
																	<div className={`form-group ${meta.touched && (meta.error ? 'has-error has-danger' : '')}`}>
																		<label htmlFor="amount_paid">Transfer Amount</label>
																		<input
																			{...input}
																			id="amount_paid"
																			type="number"
																			className="form-control"
																			placeholder="Enter transfer amount"
																			min="0"
																			max={totalAmount}
																			step="0.01"
																			onChange={e => {
																				const val = parseFloat(e.target.value || '0');
																				if (val > totalAmount) {
																					notifyError('Transfer amount cannot be more than transaction amount');
																					return;
																				}
																				input.onChange(e);
																				setAmountPaid(val);
																			}}
																		/>
																		{meta.touched && meta.error && (
																			<div className="help-block form-text with-errors form-control-feedback">
																				<ul className="list-unstyled">
																					<li>{meta.error}</li>
																				</ul>
																			</div>
																		)}
																	</div>
																)}
															/>
														</div>
													</div>

													<div className="form-buttons-w text-right">
														<button className="btn btn-secondary ml-3" type="button" onClick={onClose}>
															Cancel
														</button>
														<button className="btn btn-primary" type="submit">
															Transfer
														</button>
													</div>
												</form>
											)}
										/>
									</div>
								</div>
							</div>
						</div>
					</div>
				);
			};

			return <TransferModal />;
		},
	});
};

export const renderSelectWithDefault = ({
	input,
	label,
	placeholder,
	id,
	defaultVal,
	data,

	meta: { touched, error },
}) => {
	return (
		<div className={`form-group ${touched && (error ? 'has-error has-danger' : '')}`}>
			<label htmlFor={id}>{label}</label>
			<select {...input} className="form-control">
				<option value="">{placeholder}</option>
				{data.map((d, i) => {
					let def = d.id === defaultVal;
					return (
						<option key={i} value={d.id} defaultChecked={def}>
							{d.name}
						</option>
					);
				})}
			</select>
			{touched && error && (
				<div className="help-block form-text with-errors form-control-feedback">
					<ul className="list-unstyled">
						<li>{error}</li>
					</ul>
				</div>
			)}
		</div>
	);
};

export const renderSelect = ({ input, label, placeholder, id, data, meta: { touched, error } }) => (
	<div className={`form-group ${touched && (error ? 'has-error has-danger' : '')}`}>
		<label htmlFor={id}>{label}</label>
		<select {...input} className="form-control">
			<option value="">{placeholder}</option>
			{data.map((d, i) => (
				<option key={i} value={d.id}>
					{d.name}
				</option>
			))}
		</select>
		{touched && error && (
			<div className="help-block form-text with-errors form-control-feedback">
				<ul className="list-unstyled">
					<li>{error}</li>
				</ul>
			</div>
		)}
	</div>
);

const firstLetter = item => (item && item !== '' ? `${item.substring(0, 1)}.` : '');

const parseDuty = item => {
	if (!item || item === '') return '';
	const clean = item.replace(/\s*\(hos\)\s*/gi, '').trim();
	return ` [${uppercase(clean)}]`;
};

export const normalizeShift = duty => {
	if (!duty || duty === 'O') return 'Off';
	const clean = duty
		.toLowerCase()
		.replace(/\s*\(hos\)\s*/gi, '')
		.trim();
	if (clean === '' || clean === 'o' || clean === 'off') return 'Off';
	if (clean === 'overtime') return 'Overtime';
	if (/^(l|pl|ml)$/i.test(clean) || clean.endsWith('leave')) return 'Leave';
	if (clean === 'm' || clean === 'morning') return 'Morning';
	if (clean === 'n' || clean === 'night') return 'Night';
	return 'Off';
};

export const parseClass = item => {
	const val = (item || '')
		.toLowerCase()
		.replace(/\s*\(hos\)\s*/gi, '')
		.trim();
	if (val === 'off' || !val) {
		return 'bg-secondary';
	} else if (val === 'morning') {
		return 'bg-primary';
	} else if (val === 'night') {
		return 'bg-danger';
	} else if (val === 'leave') {
		return 'bg-warning';
	} else if (val === 'overtime') {
		return 'bg-success';
	} else {
		return 'bg-info';
	}
};

export const parseRoster = result => {
	let rosters = [];
	result.forEach(item => {
		item.schedule.forEach(schedule => {
			if (schedule.duty !== '' && schedule.duty !== null && schedule.duty !== undefined) {
				const name = `${startCase(item.staff?.last_name)} ${firstLetter(startCase(item.staff?.first_name))}`;
				const hosSuffix = schedule.is_hos ? ' (HOS)' : '';
				const title = `${name}${parseDuty(schedule.duty)}${hosSuffix}`;
				const date = `${item.period}-${schedule.date !== '' ? padLeft(schedule.date, 2, '0') : ''}`;
				const cleanDuty = (schedule.duty || '').replace(/\s*\(hos\)\s*/gi, '').trim();
				const baseClass = parseClass(schedule.duty);
				const hosClass = schedule.is_hos ? ' hos-event' : '';
				rosters = [
					...rosters,
					{
						title,
						date,
						className: `${baseClass}${hosClass}`,
						extendedProps: {
							duty: cleanDuty || '',
							is_hos: schedule.is_hos || false,
							roster_id: item.id,
							schedule_date: schedule.date,
							staff_id: item.staff?.id,
							staff_name: name,
						},
					},
				];
			}
		});
	});
	return rosters;
};

export const getUser = async () => {
	const date = new Date();
	// prettier-ignore
	const user = await (new SSRStorage()).getItem(TOKEN_COOKIE);
	if (user) {
		const token = user.token;
		const decoded = JwtDecode(token);
		if (decoded.exp > date.getTime() / 1000) {
			return user;
		}
	}

	return null;
};

export const redirectToPage = (role, history, permissions) => {
	let uri = '';
	try {
		switch (role.slug) {
			case 'front-desk':
				uri = hasViewAppointmentPermission(permissions) ? '/front-desk/appointments/queue' : '/front-desk/patients';
				break;
			case 'hr-manager':
				uri = '/hr/overview';
				break;
			case 'inventory':
				uri = '/inventory/categories';
				break;
			case 'cafeteria-waiter':
				uri = '/cafeteria';
				break;
			case 'cafeteria-kitchen':
			case 'cafeteria-manager':
			case 'cafeteria-sales':
			case 'cook':
			case 'executive-chef':
			case 'sue-chef':
			case 'steward':
				uri = '/cafeteria/orders';
				break;
			case 'cafeteria-store':
				uri = '/cafeteria/inventory';
				break;
			case 'it-admin':
				uri = '/settings';
				break;
			case 'hmo-officer':
				uri = '/hmo';
				break;
			case 'paypoint':
				uri = '/paypoint';
				break;
			case 'lab-manager':
			case 'lab-supervisor':
			case 'lab-user':
				uri = '/lab';
				break;
			case 'radiology':
				uri = '/radiology';
				break;
			case 'nurse':
				uri = '/nurse';
				break;
			case 'doctor':
			case 'hmo-doctor':
			case 'medical-director':
				uri = '/doctor';
				break;
			case 'pharmacy':
				uri = '/pharmacy';
				break;
			case 'pharmacy-supervisor':
				uri = '/report/pharmacy';
				break;
			case 'ivf':
				uri = '/ivf';
				break;
			case 'records':
				uri = '/records';
				break;
			case 'report':
				uri = '/report';
				break;
			case 'accounts':
				uri = '/accounting';
				break;
			case 'store':
				uri = '/store';
				break;
			case 'radiologist':
				uri = '/radiology';
				break;
			case 'therapist':
				uri = '/therapy';
				break;
			case 'ophthalmologist':
				uri = '/procedure';
				break;
			default:
				uri = hasViewAppointmentPermission(permissions) ? '/front-desk/appointments/queue' : '/my-account/my-attendance';
				break;
		}
	} catch (e) {
		uri = hasViewAppointmentPermission(permissions) ? '/front-desk/appointments/queue' : '/my-account/my-attendance';
	}

	if (uri !== '') {
		history.push(uri);
	} else {
		history.push('/?not-authenticated');
	}
};

export const staffname = user =>
	user ? `${startCase(user?.last_name)} ${startCase(user?.first_name)} ${startCase(user?.other_names)}` : '--';

export const patientname = (user, pid = false) => {
	const patientId = pid ? `(${formatPatientId(user)})` : '';

	return user ? startCase(`${user.other_names} ${user.surname} ${patientId}`) : '--';
};

export const patientIdentity = (user, pid = false) => {
	const patientId = pid ? `${formatPatientId(user)}` : '';

	return user ? `${patientId}` : '--';
};

export const patientnokname = user => {
	return user ? `${user.other_names} ${user.surname}` : '--';
};

export const formatNumber = n => parseFloat(n).toLocaleString(undefined, { maximumFractionDigits: 2 });

export const formatDate = (date, format = 'YYYY-MM-DD') => (date ? momentTimezone.tz(date, 'Africa/Lagos').format(format) : '--');

export const trimText = (str, length = 50, omission = '...') => truncate(str, { length, omission });

export const getPeriod = () => {
	const qtr = moment().format('Qo');
	const start = moment().startOf('quarter').format('MMM');
	const end = moment().endOf('quarter').format('MMM');

	return `${qtr} Quarter [${start} - ${end}]`;
};

export const errorMessage = error => {
	return (
		error && (
			<div
				className="alert alert-danger"
				dangerouslySetInnerHTML={{
					__html: `<strong>Error!</strong> ${error}`,
				}}
			/>
		)
	);
};

export const findByID = (array, id) => {
	return array.find(item => item.id === id);
};

export const getAge = dateString => {
	const dob = formatToStandardDate(dateString);
	if (!dob) {
		return 0;
	}

	const date = moment(new Date(dob));

	const years = moment().diff(date, 'years', false);
	const days = moment().diff(date.add(years, 'years'), 'days', false);

	if (years === 0) {
		return `${days} days`;
	}

	return `${years} years`;
};

// export const getAge = dob => {
// 	if (!dob) {
// 		return 0;
// 	}

// 	// Define the possible date formats
// 	const formats = ['YYYY-MM-DD', 'DD-MM-YYYY'];

// 	// Parse the date with the specified formats
// 	const date = moment(dob, formats, true);

// 	// Check if the date is valid
// 	if (!date.isValid()) {
// 		return 'Invalid date format';
// 	}

// 	const years = moment().diff(date, 'years', false);
// 	const adjustedDate = date.add(years, 'years'); // Adjust the date after calculating years
// 	const days = moment().diff(adjustedDate, 'days', false);

// 	if (years === 0) {
// 		return `${days} days`;
// 	}

// 	return `${years} years`;
// };

export const groupBy = function (xs, key) {
	return xs.reduce(function (rv, x) {
		(rv[x[key]] = rv[x[key]] || []).push(x);
		return rv;
	}, {});
};

export const checkVaccine = data => {
	if (!data.date_administered) {
		if (moment().isSame(data.date_due, 'day')) {
			return 'yellow';
		} else if (moment().isAfter(data.date_due)) {
			return 'red';
		} else if (moment().isBefore(data.date_due)) {
			return 'grey';
		}
	}
	return 'green';
};

export const vaccineNotDue = data => {
	return !data.appointment_date && !data.date_administered && moment().isBefore(data.date_due);
};

export const vaccineMissed = data => {
	return !data.appointment_date && !data.date_administered && !moment().isSame(data.date_due, 'day') && moment().isAfter(data.date_due);
};

export const vaccineFilledAndPaid = data => {
	const medicationRequests = data?.medicationRequests || [];
	return medicationRequests.some(req => req.items && req.items.some(item => item.filled && item.transaction?.status === 1));
};

export const hasExpired = date => {
	if (date) {
		return moment().isAfter(moment(date, 'YYYY-MM-DD'));
	}

	return false;
};

export const hasPassed = date => {
	if (date) {
		return moment(date, 'YYYY-MM-DD HH:mm:ss').isBefore(moment().startOf('day'));
	}

	return false;
};

export const itemRender = (current, type, originalElement) => {
	if (type === 'prev') {
		return <a>Previous</a>;
	}
	if (type === 'next') {
		return <a>Next</a>;
	}
	return originalElement;
};

export const nth = n => {
	const _nth = [, 'st', 'nd', 'rd'][(n % 100 >> 3) ^ 1 && n % 10] || 'th';
	return `${n}${_nth} Floor`;
};

export const parseAvatar = avatar => {
	let result = placeholder;
	if (avatar) {
		if (avatar.startsWith('http://')) {
			result = avatar;
		} else if (avatar.startsWith('https://')) {
			result = avatar;
		} else if (avatar?.includes('/images/') || avatar?.includes('/dojah/') || avatar?.includes('/avatars/')) {
			result = `${API_URI}/${avatar}`;
		} else {
			result = `${API_URI}/images/${avatar}`;
		}
	}
	return result;
};

export const parseDojahAvatar = avatar => {
	return avatar ? `${API_URI}/dojah/${avatar}` : placeholder;
};

export const parseNote = (note, hasHeading = false) => {
	if (note.type === 'diagnosis') {
		return `${note.diagnosis.description} - ${note.diagnosis.type}`;
	}

	if (note.type === 'allergy') {
		const generic = note?.generic?.name || '--';
		return `${note.allergy} [${note.category}${note.category === 'Drug' ? ` - ${generic}` : ''}] - ${note.reaction} (${note.severity})`;
	}

	if (note.type === 'patient-history') {
		const title = startCase(note.category);
		const keys = Object.keys(note.history);
		const values = Object.values(note.history).map((name, i) => `<tr><td>${startCase(keys[i])}</td><td>${name}</td></tr>`);
		const heading = hasHeading ? `</br><span class="text-underline">${title.replaceAll('history', '')}</span></br>` : '';
		return `${heading}<table class="table-sm table-bordered table-custom">${values.join('')}</table>`;
	}

	return note.description;
};

export const parseSource = source => (source === 'ward' ? 'Room' : startCase(source?.replace(/ deda/gi, '')));

export const getGestationAge = date => {
	const weeks = moment().diff(moment(date), 'weeks');
	const days = moment().diff(moment(date).add(weeks, 'w'), 'days');
	const display = `${days}day${days > 1 ? 's' : ''}`;
	return `${weeks}week${weeks > 1 ? 's' : ''} ${days > 0 ? display : ''}`;
};

export const parseFather = data => {
	const phone = data?.phone || '';
	const blood_group = data?.blood_group || '';

	const phone_number = phone !== '' ? `, Phone: ${phone}` : '';
	const bgroup = blood_group !== '' ? `, Blood Group: ${blood_group}` : '';

	return `Name: ${data?.name || '--'}${phone_number}${bgroup}`;
};

export const getCustomGestationAge = (date, lmp) => {
	const lmpDate = moment(lmp, 'YYYY-MM-DD');

	const weeks = moment(date).diff(lmpDate, 'weeks');
	const days = moment(date).diff(lmpDate.add(weeks, 'w'), 'days');

	const display = `${days}day${days > 1 ? 's' : ''}`;
	return weeks > 0 ? `${weeks}week${weeks > 1 ? 's' : ''} ${days > 0 ? display : ''}` : display;
};

export const parseFrequency = (frequency, duration) => {
	const s = duration > 1 ? 's' : '';
	const frequencies = [
		{ name: 'as-needed', value: 'As Needed' },
		{ name: 'at-night', value: `Night${s}` },
		{ name: 'immediately', value: 'Immediately' },
		{ name: 'hourly', value: `Hour${s}` },
		{ name: 'daily', value: `Day${s}` },
		{ name: 'weekly', value: `Week${s}` },
		{ name: 'monthly', value: `Month${s}` },
		{ name: 'quarterly', value: `Quarter${s}` },
		{ name: 'stat', value: 'Stat' },
	];

	const item = frequencies.find(f => f.name === frequency);

	if (frequency === 'as-needed' || item.value === 'immediately') {
		return item?.value || '';
	}

	return `for ${duration} ${item?.value || ''}`;
};

export const qsParse = querystring => {
	return Object.fromEntries(new URLSearchParams(querystring));
};

export const Compulsory = () => {
	return <span className="compulsory-field">*</span>;
};

export const ErrorBlock = ({ name }) => (
	<Field
		name={name}
		subscription={{ touched: true, error: true }}
		render={({ meta: { touched, error } }) => (touched && error ? <small className="text-danger">{error}</small> : null)}
	/>
);

export const ReactSelectAdapter = ({ input, ...rest }) => <Select {...input} {...rest} searchable />;

export const Condition = ({ when, is, children }) => (
	<Field name={when} subscription={{ value: true }}>
		{({ input: { value } }) => (value === is ? children : null)}
	</Field>
);

export const ConditionNot = ({ when, isNot, children }) => (
	<Field name={when} subscription={{ value: true }}>
		{({ input: { value } }) => (value !== isNot ? children : null)}
	</Field>
);

export const countDate = ({ start_date, end_date }) => {
	return moment(end_date, 'YYYY-MM-DD').diff(moment(start_date, 'YYYY-MM-DD'), 'days');
};

export const billItem = transaction => {
	const reqItem = transaction.patientRequestItem;

	let title = parseSource(transaction.bill_source);

	if (
		transaction?.bill_source?.includes('accommodation') ||
		transaction?.bill_source === 'ward' ||
		transaction?.bill_source === 'nicu-accommodation' ||
		transaction?.bill_source === 'credit-deposit' ||
		transaction?.bill_source === 'debit-charge'
	) {
		title = `${title}- ${transaction.description}`;
	}

	if (
		transaction?.bill_source?.startsWith('consulta') ||
		transaction?.bill_source?.startsWith('procedure') ||
		transaction?.bill_source?.startsWith('radiology') ||
		transaction?.bill_source?.startsWith('nursing') ||
		transaction?.bill_source === 'consultancy' ||
		transaction?.bill_source === 'scans' ||
		transaction?.bill_source === 'procedure' ||
		transaction?.bill_source === 'nursing-service'
	) {
		title = transaction.service?.item?.name ? `${title}- ${transaction.service?.item?.name}` : '';
	}

	if (transaction?.bill_source === 'labs') {
		title = transaction.service?.item?.labTest?.name ? `${title}- ${transaction.service?.item?.labTest?.name}` : '';
	}

	if (transaction?.bill_source === 'drugs') {
		title = `${title}- ${reqItem.fill_quantity} ${reqItem.drug.unitOfMeasure} of ${reqItem.drugGeneric.name} (${
			reqItem.drug.name
		}) at ${formatCurrency(reqItem.drugBatch.unitPrice)} each`;
	}

	return title;
};

export const print = async (transaction, items) => {
	try {
		const date = formatDate(transaction.createdAt, 'DD-MMM-YYYY');
		const payment_method = transaction.payment_method;

		let customer = '';
		if (transaction.dedastaff) {
			customer = staffname(transaction.dedastaff);
		} else if (transaction.patient) {
			customer = patientname(transaction.patient);
		} else {
			customer = 'Guest';
		}

		const total_amount = Math.abs(Number(transaction.amount));
		const vat = total_amount * Number(VAT);
		const subTotal = formatCurrencyBare(total_amount - vat, true);
		const amount = formatCurrencyBare(total_amount);

		const paid = formatCurrencyBare(transaction.amount_paid);
		const change = formatCurrencyBare(transaction.change);

		const rs = await axios.get(
			`${PAYPOINT}/receipt?date=${date}&payment_method=${payment_method}&name=${customer}&sub_total=${subTotal}&vat=${vat}&amount=${amount}&paid=${paid}&change=${change}&items=${items}`
		);
		console.log(rs.data);
	} catch (e) {
		console.log(e);
		notifyError('could not print receipt');
	}
};

export const removeNullData = async data => {
	return data.totalFollicles !== null || data.measurement !== null;
};

export const parseCharts = transaction => {
	if (!transaction) {
		return '--';
	}

	if (transaction?.bill_source === 'ward' || transaction?.bill_source === 'nicu-accommodation') {
		return `${startCase(transaction?.description)}`;
	}

	const reqItem = transaction.patientRequestItem;
	if (transaction?.bill_source === 'drugs') {
		return `${reqItem?.drug?.unitOfMeasure} of ${reqItem?.drugGeneric?.name} (${reqItem?.drug?.name})`;
	}

	if (transaction?.bill_source === 'labs') {
		return `${startCase(reqItem?.labTest?.name)}`;
	}

	if (transaction?.service?.item?.name) {
		return `${startCase(transaction.service.item.name)}`;
	}

	if (transaction?.patientRequestItem?.service?.item?.name) {
		return `${startCase(transaction.patientRequestItem.service.item.name)}`;
	}
	return '--';
};

export const returnPercentage = (total, value) => {
	return 100 - Math.abs((value / total) * 100).toFixed(0);
};

export const getDiscountPerc = (amount, discount_amount) => {
	const perc = 100 - (Number(discount_amount) / Number(amount)) * 100;
	return perc?.toFixed(1);
};

export const getPercentage = (total, amount) => {
	const perc = 100 - (Number(amount) / Number(total)) * 100;
	return perc?.toFixed(1);
};

export const getPercentageAmount = (amount, perc) => {
	return ((parseFloat(amount) / 100) * parseFloat(perc)).toFixed(2);
};

export const getRatingValue = (rate, weight, maxRating = 5) => {
	return Math.round((parseFloat(weight) / parseFloat(maxRating)) * parseFloat(rate));
};

export const getGrade = (score, maxScore = 100) => {
	const pct = maxScore > 0 ? (score / maxScore) * 100 : 0;
	if (pct >= 96) return { letter: 'A', label: 'Excellent', color: '#d4edda' };
	if (pct >= 80) return { letter: 'B', label: 'Very Good', color: '#e2f0d9' };
	if (pct >= 70) return { letter: 'C', label: 'Satisfactory', color: '#fff3cd' };
	if (pct >= 50) return { letter: 'D', label: 'Below Expectation', color: '#ffeeba' };
	return { letter: 'E', label: 'Unacceptable', color: '#f8d7da' };
};

export const computeWeightedScore = (fields = [], sections = []) => {
	const useWeighted = sections.length && sections.some(s => s.maxRating);
	if (!useWeighted) {
		return Math.round(fields.reduce((sum, f) => sum + (f.rating != null ? Number(getRatingValue(f.rating, f.weight)) : 0), 0));
	}
	const defaultSectionId = sections[0]?.id;
	return Math.round(
		sections.reduce((total, section) => {
			const maxRating = section.maxRating || 5;
			const sectionFields = fields.filter(f => (f.sectionId || defaultSectionId) === section.id);
			const sectionScore = sectionFields.reduce(
				(sum, f) => sum + (f.rating != null ? Number(getRatingValue(f.rating, f.weight, maxRating)) : 0),
				0
			);
			return total + (sectionScore * (Number(section.targetWeight) || 0)) / 100;
		}, 0)
	);
};

export const computeWeightedSupervisorScore = (fields = [], sections = []) => {
	const useWeighted = sections.length && sections.some(s => s.maxRating);
	if (!useWeighted) {
		return Math.round(fields.reduce((sum, f) => sum + (Number(f.supervisor_score) || 0), 0));
	}
	const defaultSectionId = sections[0]?.id;
	return Math.round(
		sections.reduce((total, section) => {
			const maxRating = section.maxRating || 5;
			const sectionFields = fields.filter(f => (f.sectionId || defaultSectionId) === section.id);
			const sectionScore = sectionFields.reduce((sum, f) => {
				if (f.supervisor_rating != null) return sum + Number(getRatingValue(f.supervisor_rating, f.weight, maxRating));
				return sum + (Number(f.supervisor_score) || 0);
			}, 0);
			return total + (sectionScore * (Number(section.targetWeight) || 0)) / 100;
		}, 0)
	);
};

export const parseSourceText = item => {
	if (!item) {
		return '--';
	}

	const reqItem = item.patientRequestItem;

	const first = ['ward', 'nicu-accommodation', 'credit-deposit', 'debit-charge'];
	const second = ['consultancy', 'scans', 'procedure', 'billables', 'nursing-service'];
	const third = ['labs'];
	const fourth = ['drugs'];
	const fifth = ['cafeteria'];

	if (first.includes(item.bill_source) || item?.bill_source?.includes('accommodation')) {
		return `: ${item.description}`;
	} else if (
		second.includes(item.bill_source) ||
		item.bill_source?.startsWith('consulta') ||
		item.bill_source?.startsWith('procedure') ||
		item.bill_source?.startsWith('radiology') ||
		item.bill_source?.startsWith('billable') ||
		item.bill_source?.startsWith('nursing')
	) {
		return `: ${item.service?.item?.name}`;
	} else if (third.includes(item.bill_source)) {
		return `: ${item.service?.item?.labTest?.name || item.service?.item?.name || '--'}`;
	} else if (fourth.includes(item.bill_source)) {
		return ` : ${reqItem?.fill_quantity} ${reqItem?.drug?.unitOfMeasure ?? 'no drug name'} of ${reqItem?.drugGeneric?.name ?? 'no name'} (${
			reqItem?.drug?.name ?? 'no name'
		}) at ${formatCurrency(item?.service?.tariff || 0)} each`;
	} else if (fifth.includes(item.bill_source)) {
		return `: ${item.transaction_details?.map(t => `${t.name} (${t?.qty || 1})`).join(', ')}`;
	} else {
		return `: ${item.description}`;
	}
};

export const renderServiceSource = (transaction, { showAdmission } = {}) => {
	if (!transaction) return null;
	const source = parseSource(transaction.bill_source);
	const text = parseSourceText(transaction);
	const hasAdmission = !!(transaction.admission || transaction.admission_id);

	return (
		<>
			<strong>{source}</strong>
			{showAdmission && hasAdmission && <i className="fa fa-bed text-danger" style={{ margin: '0 4px' }} />}
			{text}
		</>
	);
};

export const getRandomColors = name => {
	const colors = ['success', 'primary', 'danger', 'info', 'warning', 'secondary'];
	const firstLetter = name[0]?.toUpperCase();
	const index = alphabets.indexOf(firstLetter) % 5;
	return colors[index];
};

export const camelCaseToSentenceCase = str => {
	let result = '';
	for (let i = 0; i < str?.length; i++) {
		if (alphabets.includes(str[i])) {
			result += ` ${str[i].toUpperCase()}`;
		} else {
			if (i === 0) {
				result += str[i].toUpperCase();
			} else {
				result += str[i];
			}
		}
	}
	return result;
};

export const validateEmail = email => emailRegex.test(email);
export const validatePhone = phone => phoneRegex.test(phone);

export const getFormattedGender = gender => {
	if (gender?.toLowerCase() === 'm') {
		return 'Male';
	} else if (gender?.toLowerCase() === 'f') {
		return 'Female';
	} else if (['male', 'female'].includes(gender?.toLowerCase())) {
		return startCase(gender);
	} else {
		return '';
	}
};

export const getLeaveStatus = (leave, queryByAdmin = false) => {
	const staffIsSupervisor = Boolean(
		(leave.staff?.department?.staff || leave.staff?.department?.hod_id) &&
			(leave.staff?.department?.hod_id || leave.staff?.department?.staff?.id) === leave.staff?.id
	);
	let result = {
		statusText: 'Pending',
		statusColor: 'yellow',
		hrCanEdit: false,
		supCanEdit: false,
		adminCanApprove: false,
		staffIsSupervisor,
	};
	if (!leave) {
		return result;
	}
	if (leave.supervisor_approved && leave.status === 0) {
		result.hrCanEdit = true;
		result.statusColor = 'yellow';
		result.statusText = 'Pending HR Approval';
	}
	if (!leave.supervisor_approved && leave.status === 0) {
		result.supCanEdit = true;
		result.statusColor = 'yellow';
		result.statusText = 'Pending Supervisor Approval';
	}
	if (leave.status === 1) {
		result.supCanEdit = false;
		result.hrCanEdit = false;
		result.statusColor = 'green';
		result.statusText = 'Approved';
	}
	if (leave.status === -1) {
		result.supCanEdit = false;
		result.hrCanEdit = false;
		result.statusColor = 'red';
		result.statusText = 'Declined';
	}
	if (leave.status === -1 && !leave.supervisor_approved) {
		result.supCanEdit = false;
		result.hrCanEdit = false;
		result.statusColor = 'red';
		result.statusText = 'Declined By Supervisor';
	}
	if (leave.status === -1 && leave.supervisor_approved) {
		result.supCanEdit = false;
		result.hrCanEdit = false;
		result.statusColor = 'red';
		result.statusText = 'Declined By HR';
	}
	if (moment().isBetween(moment(leave.start_date), moment(leave.end_date))) {
		result.supCanEdit = false;
		result.hrCanEdit = false;
		result.statusColor = 'green';
		result.statusText = 'Active';
	}
	if (leave.supervisor_approved && staffIsSupervisor && leave.status === 0 && queryByAdmin) {
		result.adminCanApprove = true;
		result.supCanEdit = false;
		result.hrCanEdit = false;
		result.statusColor = 'yellow';
		result.statusText = 'Pending Admin Approval';
	}
	if (leave.supervisor_approved && staffIsSupervisor && leave.status === 0) {
		result.statusText = 'Pending Admin Approval';
	}
	if (leave.supervisor_approved && staffIsSupervisor && leave.status === -1) {
		result.statusText = 'Declined By Admin';
	}

	return result;
};

export const formatToStandardDate = dateStr => {
	if (!dateStr) {
		return null;
	}
	const testStrSplit = dateStr?.split('-');

	if (testStrSplit.length !== 3) {
		return null;
	}

	let day;
	let month;
	let year;

	if (testStrSplit[0].length <= 2) {
		day = testStrSplit[0];
		month = testStrSplit[1];
		year = testStrSplit[2];
	} else if (testStrSplit[0].length === 4) {
		day = testStrSplit[2];
		month = testStrSplit[1];
		year = testStrSplit[0];
	}
	const newDateStr = `${year}-${month}-${day}`;
	// console.log({ newDateStr });
	let date = moment(newDateStr);
	return date.isValid() ? date.format('YYYY-MM-DD') : null;
};

export const generateMediaRoomId = ({ patientId, doctorId }) => {
	return `${patientId}_${doctorId}`;
};

export const formatDuration = seconds => {
	const duration = moment.duration(seconds, 'seconds');
	if (seconds < 60) {
		return `${seconds}s`;
	} else if (seconds < 3600) {
		// less than 1 hour
		return `${duration.minutes()}m ${duration.seconds()}s`;
	} else if (seconds < 86400) {
		// less than 1 day
		return `${duration.hours()}h ${duration.minutes()}m`;
	} else {
		return `${duration.days()}d ${duration.hours()}h`;
	}
};

export const mapTimeRangeToApiFilterAlt = (range, year) => {
	switch (range) {
		case 'today':
			return 'Today';
		case 'past-week':
			return 'Past Week';
		case 'last-month':
			return 'Last Month';
		case 'past-quarter':
			return 'Past Quarter';
		case 'past-six-months':
			return 'Past Six Months';
		case 'past-three-years':
			return 'Past Three Years';
		case 'past-month':
			return 'Past 30 Days';
		case 'past-year':
			return 'Past Year';
		case 'last-year':
			return 'Last Year';
		case 'select-year':
			return year || 'Select Year';
		case 'all-time':
			return 'All Time';
		default:
			return 'Past 30 Days';
	}
};

export const mapTimeRangeToApiFilterAlt2 = range => {
	switch (range) {
		case 'today':
			return 'Today';
		case 'pastWeek':
			return 'Past Week';
		case 'lastMonth':
			return 'Last Month';
		case 'pastQuarter':
			return 'Past Quarter';
		case 'pastSixMonths':
			return 'Past Six Months';
		case 'pastThreeYears':
			return 'Past Three Years';
		case 'past30Days':
			return 'Past 30 Days';
		case 'pastYear':
			return 'Past Year';
		case 'lastYear':
			return 'Last Year';
		case 'allTime':
			return 'All Time';
		default:
			return 'Past 30 Days';
	}
};

// Metrics data
export const colorMetrics = {
	totalRequests: '#0088FE',
	approved: '#22BB33',
	filled: '#00C49F',
	received: '#33CCFF',
	notApproved: '#8884d8',
	notFilled: '#A28EFF',
	substituted: '#FFBB28',
	notSubstituted: '#FFBB28',
	cancelled: '#FF8042',
	cancelledOnly: '#FF6699',
	notReceived: '#FF8042',
	deleted: '#FF4444',
};

export const MetricCard = ({ loading, value, label, total, filterKey, filters, metricKey }) => {
	const percentage = value != null && total > 0 ? ((value / total) * 100).toFixed(1) : 0;
	const color = colorMetrics[metricKey] || '#000';
	return (
		<div className="col-sm-3">
			<a className="element-box el-tablo" href="#">
				<Statistic
					title={label}
					value={loading ? <TableLoading /> : value != null ? Number(value).toLocaleString() : '0'}
					valueStyle={{ fontWeight: 'bold', color }}
				/>
				{label !== 'Total Requests' ? (
					<small className="text-muted">
						<strong style={{ color }}>{percentage}%</strong> of Total Requests for{' '}
					</small>
				) : (
					<small className="text-muted">Total Requests</small>
				)}{' '}
				<small className="text-muted">
					<strong>{mapTimeRangeToApiFilterAlt(filters?.[filterKey]?.dateFilter)}</strong>
				</small>
			</a>
		</div>
	);
};

export const metricsConfig = [
	{ key: 'totalRequests', label: 'Total Requests' },
	{ key: 'cancelledOnly', label: 'Cancelled Only' },
	{ key: 'cancelled', label: 'Cancelled' },
	{ key: 'approved', label: 'Approved' },
	{ key: 'notApproved', label: 'Not Approved' },
	{ key: 'filled', label: 'Filled' },
	{ key: 'notFilled', label: 'Not Filled' },
	{ key: 'substituted', label: 'Substituted' },
	{ key: 'notSubstituted', label: 'Not Substituted' },
	{ key: 'received', label: 'Received' },
	{ key: 'notReceived', label: 'Not Received' },
	{ key: 'deleted', label: 'Deleted' },
];

export const capitalizeHyphenated = str => {
	return str
		?.split('-')
		?.map(word => word.charAt(0).toUpperCase() + word.slice(1))
		?.join('-');
};

export const capitalizeWords = (str = '') =>
	str
		?.trim()
		?.toLowerCase()
		?.replace(/\b\w/g, char => char.toUpperCase());

export const capitalizeAndRemoveHyphen = (input = '') => {
	let str = '';

	if (input == null) {
		str = '';
	} else if (typeof input === 'string' || typeof input === 'number') {
		str = String(input);
	} else if (typeof input === 'object') {
		if (input.name) str = String(input.name);
		else if (input.value) str = String(input.value);
		else if (input.payload && (input.payload.name || input.payload.value)) {
			str = String(input.payload.name || input.payload.value);
		} else {
			try {
				str = JSON.stringify(input);
			} catch {
				str = '';
			}
		}
	} else {
		str = String(input);
	}

	return str
		.trim()
		.replace(/[_-]+/g, ' ')
		.toLowerCase()
		.split(/\s+/)
		.filter(Boolean)
		.map(word => word.charAt(0).toUpperCase() + word.slice(1))
		.join(' ');
};

export const getFilterValue = (filters, key) => {
	const filterGroup = filters?.[key];
	if (!filterGroup) return null;
	return filterGroup?.dateFilter === 'select-year' ? filterGroup?.year : filterGroup?.dateFilter;
};

export const cardColors = [
	{ bg: 'blue', text: '#fff' },
	{ bg: 'green', text: '#fff' },
	{ bg: 'brown', text: '#fff' },
	{ bg: 'red', text: '#fff' },
];

export const mapTimeRangeToApiFilterAlt3 = range => {
	if (Number.isInteger(Number(range)) && range.toString().length === 4) {
		return range;
	}

	switch (range) {
		case 'today':
			return 'today';
		case 'pastWeek':
			return 'past-week';
		case 'lastMonth':
			return 'last-month';
		case 'pastQuarter':
			return 'past-quarter';
		case 'pastSixMonths':
			return 'past-six-months';
		case 'pastThreeYears':
			return 'past-three-years';
		case 'past30Days':
			return 'past-month';
		case 'pastYear':
			return 'past-year';
		case 'lastYear':
			return 'last-year';
		case 'allTime':
			return 'all-time';
		default:
			return 'past-month';
	}
};

export const getRequestStatus = order => {
	if (!order || !order.item) return { label: 'Unknown', color: 'secondary' };

	const { item, status: reqStatus, requestType } = order;
	const tx = item.transaction;

	if (item.cancelled === 1) return { label: 'Cancelled', color: 'danger' };

	switch (requestType) {
		case 'labs': {
			if (reqStatus === 1 && item.approved === 1) {
				return item.reviewed === 1 ? { label: 'Reviewed', color: 'success' } : { label: 'Approved', color: 'success' };
			}
			if (!tx || tx.status === 0) return { label: 'Awaiting Payment', color: 'warning' };
			if ((tx.status === 1 || tx.status === -1) && item.filled === 0) {
				return item.received === 0 ? { label: 'Awaiting Specimen', color: 'info' } : { label: 'Awaiting Result', color: 'info' };
			}
			if ((tx.status === 1 || tx.status === -1) && item.filled === 1 && item.approved === 0) {
				return { label: 'Awaiting Approval', color: 'secondary' };
			}
			return { label: 'Pending', color: 'warning' };
		}

		case 'scans':
		case 'radiology-deda': {
			if (reqStatus === 1 && item.approved === 1) {
				return item.reviewed === 1 ? { label: 'Reviewed', color: 'success' } : { label: 'Approved', color: 'success' };
			}
			if (!tx || tx.status !== 1) return { label: 'Awaiting Payment', color: 'warning' };
			if (tx.status === 1 && item.received === 0) return { label: 'Pending', color: 'info' };
			if (tx.status === 1 && item.received === 1 && item.approved === 0) {
				return { label: 'Awaiting Approval', color: 'secondary' };
			}
			return { label: 'Pending', color: 'warning' };
		}

		case 'drugs': {
			if (reqStatus === 1) return { label: 'Completed', color: 'success' };
			if (reqStatus === 2 && item.filled === 0) return { label: 'Returned', color: 'primary' };
			if (reqStatus === 0 && item.filled === 0) return { label: 'Pending', color: 'warning' };
			if ((!tx || tx.status === 0) && reqStatus === 0 && item.filled === 1) {
				return { label: 'Awaiting Payment', color: 'info' };
			}
			if (tx && tx.status === 1 && reqStatus === 0) return { label: 'Awaiting Dispense', color: 'secondary' };
			return { label: 'Pending', color: 'warning' };
		}

		case 'procedure':
		case 'procedure-deda': {
			const isAwaitingPayment = !tx || tx.status === 0 || reqStatus === 0;
			const isScheduled = !!item.scheduledDate;
			const isStarted = !!item.startedDate;
			const isFinished = !!item.finishedDate;

			if (isAwaitingPayment) return { label: 'Awaiting Payment', color: 'warning' };
			if (!isScheduled) return { label: 'Open', color: 'secondary' };
			if (isScheduled && !isStarted) return { label: 'Scheduled', color: 'primary' };
			if (isStarted && !isFinished) return { label: 'Started', color: 'info' };
			if (isFinished) return { label: 'Concluded', color: 'success' };
			return { label: 'Open', color: 'secondary' };
		}

		case 'nursing-service': {
			if (reqStatus === 1) return { label: 'Completed', color: 'success' };
			if (!tx || tx.status === 0) return { label: 'Awaiting Payment', color: 'warning' };
			if (tx.status === 1) return { label: 'Payment Cleared', color: 'info' };
			return { label: 'Pending', color: 'warning' };
		}

		default: {
			if (reqStatus === 1) return { label: 'Completed', color: 'success' };
			if (!tx || tx.status === 0) return { label: 'Awaiting Payment', color: 'warning' };
			if (tx.status === 1) return { label: 'Paid', color: 'info' };
			return { label: 'Pending', color: 'warning' };
		}
	}
};
